import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { Form, Message } from 'semantic-ui-react';
import messages from '../../messages';
import { useSelector } from 'react-redux';
import { RootStateOrAny } from 'src/store';
import type {
  CSVFileSelectionState,
  SiteGroupKind,
} from './siteImportModalTypes';
import { parseSiteImportFile } from './siteImportFileUtils';

export type CSVFileSelectionPropsType = {
  customClassName: string;
  groupKind: SiteGroupKind;
  selectionState: CSVFileSelectionState;
  onSelectionChange: (updates: Partial<CSVFileSelectionState>) => void;
} & WrappedComponentProps;

function CSVFileSelection(props: CSVFileSelectionPropsType) {
  const {
    customClassName,
    intl,
    groupKind,
    selectionState,
    onSelectionChange,
  } = props;
  const { formatMessage } = intl;
  const [userLimits, networkSiteCount, subscriberSiteCount] = useSelector(
    (state: RootStateOrAny) => [
      state.mainFrame.userLimits,
      state.mainFrame.networkSiteCount,
      state.mainFrame.subscriberSiteCount,
    ]
  );
  const [networkSiteNameTemplate, subscriberSiteNameTemplate] = useSelector(
    (state) => [
      state.map.networkSiteNameTemplate,
      state.map.subscriberSiteNameTemplate,
    ]
  );
  const siteNameTemplate =
    groupKind === 'network_sites'
      ? networkSiteNameTemplate
      : subscriberSiteNameTemplate;
  const count =
    groupKind === 'network_sites' ? networkSiteCount : subscriberSiteCount;
  const { error, fileLoading } = selectionState;

  const onInputClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>
  ) => {
    const element = event.target as HTMLInputElement;
    element.value = '';
  };

  const getFileSizeError = (filesLength: number) => {
    switch (groupKind) {
      case 'network_sites': {
        if (networkSiteCount + (filesLength - 1) > userLimits.network_sites) {
          return formatMessage(messages.limitExceededError, {
            entityName: 'network site',
            limit: userLimits.network_sites,
          });
        }
        break;
      }
      case 'subscriber_sites': {
        if (
          subscriberSiteCount + (filesLength - 1) >
          userLimits.subscriber_sites
        ) {
          return formatMessage(messages.limitExceededError, {
            entityName: 'subscriber site',
            limit: userLimits.subscriber_sites,
          });
        }
        break;
      }
    }

    return null;
  };

  const fileChange = async (files: FileList | null) => {
    if (!files?.length) {
      return;
    }
    onSelectionChange({
      error: null,
      fileData: [],
      fileLoading: true,
      fileName: '',
      fileType: null,
    });

    try {
      const parsedFile = await parseSiteImportFile(files[0], {
        siteNameTemplate,
        count,
      });
      const fileSizeError = getFileSizeError(parsedFile.fileData.length);
      onSelectionChange({
        error: fileSizeError,
        fileData: parsedFile.fileData,
        fileLoading: false,
        fileName: parsedFile.fileName,
        fileType: parsedFile.fileType,
      });
    } catch (err) {
      console.error(err);
      onSelectionChange({
        error:
          err instanceof Error
            ? err.message
            : 'The data file provided is either empty or corrupt.',
        fileLoading: false,
      });
    }
  };

  return (
    <div className={customClassName}>
      <Form>
        <Form.Input
          type="file"
          icon="file"
          iconPosition="left"
          loading={fileLoading}
          accept=".csv, .kml, .kmz"
          onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) =>
            onInputClick(e)
          }
          onChange={(e) => {
            fileChange(e.target.files);
          }}
        ></Form.Input>
        {error ? (
          <Message error className="create-ptp-warning">
            <p>{error}</p>
          </Message>
        ) : null}
      </Form>
    </div>
  );
}

export default injectIntl(CSVFileSelection);
